import React from 'react';

import { withAutoFocus } from '../hocs/withAutoFocus';
import { withControl } from '../hocs/withControl';
import { withControl as withControlDesktop } from '../hocs/withControl@desktop';
import { withDebounceInput } from '../hocs/withDebounceInput';
import { useTextFieldHint } from '../hooks/useTextFieldHint';
import { useUniqueId } from '../hooks/useUniqueId';
import { compose, composeU } from '../lib/compose';
import { useRenderOverride } from '../lib/render-override';
import { withHasClear } from './_hasClear/Textinput_hasClear';
import { withSizeS } from './_size/Textinput_size_s';
import { withViewContrast } from './_view/Textinput_view_contrast';
import { TextinputBox as Box } from './Box/Textinput-Box';
import { TextinputControl as ControlOriginal } from './Control/Textinput-Control';
import { TextinputHint as Hint } from './Hint/Textinput-Hint';
import { TextinputIcon as Icon } from './Icon/Textinput-Icon';
import { cnTextinput } from './Textinput.cn';
import type { TextinputBaseProps } from './Textinput.types';

import './Textinput.css';

const enhance = compose(
    withAutoFocus,
    withControl,
    withControlDesktop,
    composeU(withViewContrast),
    composeU(withSizeS),
    withDebounceInput,
    withHasClear,
);

/**
 * @deprecated Use import { HrInput } from '@yandex-int/hr-components/HrInput'
 */
export const Textinput = enhance(
    ({
        addonAfter,
        addonBefore,
        className,
        disabled,
        focused,
        iconLeft,
        iconRight,
        innerRef,
        onMouseEnter,
        onMouseLeave,
        style,
        pressed: _pressed,
        view = 'outline',
        size = 'm',
        // @ts-expect-error
        pin: _pin,
        hint: htmlHint,
        state,
        hovered,
        title,
        renderControl,
        ...props
    }: TextinputBaseProps) => {
        React.useEffect(() => {
            if (process.env.NODE_ENV === 'development')
                console.warn('[TextInput]: Эта версия компонента устарела и скоро будет удалена');
        }, []);

        const Control = useRenderOverride(ControlOriginal, renderControl);

        const hintId = useUniqueId();
        const { hint, hintProps } = useTextFieldHint({ hint: htmlHint });

        return (
            <span
                className={cnTextinput(
                    {
                        size,
                        view,
                        disabled,
                        focused,
                        hovered,
                        iconRight: iconRight !== undefined,
                        iconLeft: iconLeft !== undefined,
                        state,
                    },
                    [className],
                )}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                ref={innerRef}
                style={style}
                title={title}
            >
                {addonBefore}
                {iconLeft && <Icon side="left" component={iconLeft} />}
                {iconRight && <Icon side="right" component={iconRight} />}
                <Control
                    {...props}
                    aria-invalid={state === 'error'}
                    disabled={disabled}
                    aria-describedby={hint ? hintId : undefined}
                />
                <Box />
                {addonAfter}
                {hint && (
                    <Hint {...hintProps} id={hintId}>
                        {hint}
                    </Hint>
                )}
            </span>
        );
    },
);

Textinput.displayName = cnTextinput();

export type TextinputProps = React.ComponentProps<typeof Textinput>;
